import { useState, useEffect, useMemo, useRef, Fragment } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../context/AuthContext'
import { createPurchase, getProjects, revisarDuplicados, registrarDecisionesDuplicado, getProveedorVigente, getPurchase, updatePurchase, uploadPurchaseAttachment, buscarProveedoresSap } from '../api/client'
import { lineTotals } from '../utils/purchaseTotals'
import { externalHref } from '../utils/url'
import { currentLocale } from '../i18n'
import Autocompletar from '../components/purchases/Autocompletar'

// New purchase request: one screen laid out like the order it becomes in SAP
// (who and for what on top, the lines in a grid, the totals at the bottom
// right) but with the guidance a requester needs. One shape for any size: a
// single line is saved as a single-item request, several as a multi-item one,
// so there is no "multiple items" switch to find.

const CURRENCIES = ['CLP', 'USD', 'EUR', 'CNY', 'BRL', 'GBP', 'AUD']
const UNITS = ['UND', 'kg', 'g', 'm', 'cm', 'mm', 'm²', 'm³', 'L', 'mL', 'hours', 'set', 'box', 'roll']
const AREAS = [
  'ADMINISTRACION Y FINANZAS', 'COMERCIAL', 'GERENCIA', 'OPERACIONES', 'REM BRASIL', 'TECNOLOGIA', 'VENTAS',
]
const TYPES = ['national', 'international']

let _uid = 0
// `final` and `currency` only come from an existing request being edited: the final price a purchaser already
// set on the line, and its own currency (a legacy request may mix them), so saving never drops either.
const emptyLine = () => ({ uid: ++_uid, id_rem: '', item_description: '', quantity: 1, unit: 'UND', price: '', final: null, currency: null })
const input = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200'
const cell = 'w-full bg-transparent px-2 py-2 text-sm focus:outline-none focus:bg-sky-50'

function fmtMoney(n, currency) {
  if (n == null || isNaN(n)) return '—'
  return `${Number(n).toLocaleString(currentLocale(), { maximumFractionDigits: 2 })} ${currency}`
}

function Field({ label, children, hint }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-gray-600 mb-1">{label}</span>
      {children}
      {hint && <span className="block text-[11px] text-gray-400 mt-1">{hint}</span>}
    </label>
  )
}

function Row({ label, children, strong }) {
  return (
    <div className={`flex items-center justify-between gap-3 py-1.5 ${strong ? 'border-t border-gray-200 mt-1 pt-2.5' : ''}`}>
      <span className={`text-sm ${strong ? 'font-semibold text-gray-900' : 'text-gray-500'}`}>{label}</span>
      <span className={`text-sm ${strong ? 'font-semibold text-gray-900' : 'text-gray-800'}`}>{children}</span>
    </div>
  )
}

const fmtQty = (c) => (c.cantidad == null ? '' : `${Number(c.cantidad).toLocaleString(currentLocale(), { maximumFractionDigits: 3 })}${c.unidad ? ` ${c.unidad}` : ''}`)

// The warning under a line that may repeat something already requested or bought (brief §10): what exists,
// and the two ways on -- say why this one is different, or take the line out.
function AlertaDuplicado({ alerta, decision, onContinuar, onQuitar, t }) {
  const [abierto, setAbierto] = useState(false)
  const [motivo, setMotivo] = useState('')
  const hace = (d) => (d == null ? '' : d === 0 ? t('newRequest.dup.hoy') : t('newRequest.dup.hace', { count: d }))

  if (decision) {
    return (
      <div className="text-xs text-gray-500 px-1">
        ✓ {t('newRequest.dup.revisado')}: <span className="text-gray-700">{decision.motivo}</span>{' '}
        <button type="button" onClick={() => onContinuar(null)} className="underline">{t('newRequest.dup.cambiar')}</button>
      </div>
    )
  }
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900" role="alert">
      <div className="font-medium">⚠ {t('newRequest.dup.titulo', { count: alerta.total })}</div>
      <ul className="mt-1 space-y-0.5 text-xs">
        {alerta.coincidencias.map((c, i) => (
          <li key={i}>
            <a href={c.tipo === 'solicitud' ? `/purchases/${c.id}` : `/compras/pedidos?abiertos=0&pedido=${c.id}`} target="_blank" rel="noreferrer"
              className="font-medium underline">
              {c.tipo === 'solicitud' ? t('newRequest.dup.solicitud', { n: c.numero }) : t('newRequest.dup.pedido', { n: c.numero })}
            </a>
            {' · '}{[c.proyecto, fmtQty(c), hace(c.dias), c.solicitante,
              c.tipo === 'solicitud' ? t(`states.${c.estado}`, c.estado) : t(`newRequest.dup.sap.${c.estado}`, c.estado)].filter(Boolean).join(' · ')}
            {c.por === 'descripcion' && <span className="text-amber-700"> ({t('newRequest.dup.parecida')})</span>}
          </li>
        ))}
        {alerta.total > alerta.coincidencias.length && <li className="text-amber-700">{t('newRequest.dup.mas', { n: alerta.total - alerta.coincidencias.length })}</li>}
      </ul>
      {!abierto ? (
        <div className="mt-2 flex flex-wrap gap-2">
          <button type="button" onClick={() => setAbierto(true)} className="px-3 py-1 rounded-md bg-white border border-amber-300 text-xs font-medium hover:bg-amber-100">{t('newRequest.dup.distinto')}</button>
          <button type="button" onClick={onQuitar} className="px-3 py-1 rounded-md bg-white border border-amber-300 text-xs font-medium hover:bg-amber-100">{t('newRequest.dup.quitar')}</button>
        </div>
      ) : (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <input autoFocus value={motivo} onChange={e => setMotivo(e.target.value)} placeholder={t('newRequest.dup.motivo')} aria-label={t('newRequest.dup.motivo')}
            className="flex-1 min-w-[14rem] border border-amber-300 rounded-md px-2 py-1 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-amber-200"
            onKeyDown={e => { if (e.key === 'Enter' && motivo.trim().length >= 3) onContinuar(motivo.trim()) }} />
          <button type="button" disabled={motivo.trim().length < 3} onClick={() => onContinuar(motivo.trim())}
            className="px-3 py-1 rounded-md bg-amber-600 text-white text-xs font-medium hover:bg-amber-700 disabled:opacity-40">{t('newRequest.dup.continuar')}</button>
          <button type="button" onClick={() => setAbierto(false)} className="text-xs underline">{t('common:actions.cancel')}</button>
        </div>
      )}
    </div>
  )
}

export default function PurchaseNew() {
  const { t } = useTranslation(['purchases', 'common'])
  const navigate = useNavigate()
  const { user } = useAuth()
  const { id: idParam } = useParams()
  const editId = idParam ? Number(idParam) : null                  // /purchases/:id/edit: the same screen, filled in
  const [original, setOriginal] = useState(null)                   // the request being edited
  const [archivos, setArchivos] = useState([])                 // files chosen to attach: { id, file }
  const archivoRef = useRef(null)
  const [mixta, setMixta] = useState(false)                        // lines in different currencies (older requests)

  const [tipo, setTipo] = useState('national')
  const [head, setHead] = useState({ project: '', area: '', supplier: '', needed_by: '', currency: 'CLP' })
  const [lines, setLines] = useState([emptyLine()])
  const [pie, setPie] = useState({ vat_included: false, discount: '', justification: '', url: '' })
  const [projects, setProjects] = useState([])
  const [projectText, setProjectText] = useState('')
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [alertas, setAlertas] = useState({})        // line uid -> { huella, total, coincidencias }: what looked repeated
  const [decisiones, setDecisiones] = useState({})  // line uid -> { huella, motivo }: carried on, saying why
  const [quitadas, setQuitadas] = useState([])      // lines taken out because of an alert (logged too)
  const focusNext = useRef(null)          // { row, col } to focus after the next render
  const gridRef = useRef(null)

  useEffect(() => {
    getProjects().then(d => setProjects(Array.isArray(d) ? d.filter(p => p.is_active) : [])).catch(() => {})
  }, [])

  // Editing: fill the screen with what the request has, every field of it.
  useEffect(() => {
    if (!editId) return undefined
    let vivo = true
    getPurchase(editId).then(r => {
      if (!vivo) return
      const items = r.is_multi
        ? [...(r.items || [])].sort((a, b) => a.position - b.position)
        : [{ id_rem: r.id_rem, item_description: r.item_description, quantity: r.quantity, unit: r.unit,
             estimated_unit_price: r.estimated_unit_price, final_unit_price: null, currency: r.currency }]
      const monedas = new Set(items.map(it => it.currency || r.currency))
      setMixta(monedas.size > 1)
      setOriginal(r)
      setTipo(r.order_type || (r.international ? 'international' : 'national'))
      setHead({ project: r.project_reference || '', area: r.area || '', supplier: r.supplier || '',
                needed_by: r.needed_by || '', currency: r.currency || [...monedas][0] || 'CLP' })
      setProjectText(r.project_reference || '')
      setPie({ vat_included: !!r.vat_included, discount: r.discount ? String(r.discount) : '', justification: r.justification || '', url: r.url || '' })
      setLines(items.length ? items.map(it => ({
        uid: ++_uid, id_rem: it.id_rem || '', item_description: it.item_description || '', quantity: it.quantity ?? 1,
        unit: it.unit || 'UND', price: it.estimated_unit_price ?? '', final: it.final_unit_price ?? null,
        currency: it.currency || r.currency || 'CLP' })) : [emptyLine()])
    }).catch(e => { if (vivo) setError(e.response?.data?.detail || t('modal.errors.createFailed')) })
    return () => { vivo = false }
  }, [editId, t])

  useEffect(() => {
    const f = focusNext.current
    if (!f) return
    focusNext.current = null
    gridRef.current?.querySelector(`[data-cell="${f.row}-${f.col}"]`)?.focus()
  }, [lines.length])

  const intl = tipo === 'international'
  const currency = intl ? head.currency : 'CLP'
  const projectLabel = (p) => `${p.code ? `${p.code} · ` : ''}${p.name}`

  const setLine = (i, patch) => setLines(ls => ls.map((l, k) => (k === i ? { ...l, ...patch } : l)))
  const addLine = (focusCol = 'item_description') => {
    focusNext.current = { row: lines.length, col: focusCol }
    setLines(ls => [...ls, emptyLine()])
  }
  const removeLine = (i) => setLines(ls => (ls.length === 1 ? [emptyLine()] : ls.filter((_, k) => k !== i)))

  // Enter walks down the same column, and adds a line from the last row.
  const onCellKey = (e, row, col) => {
    if (e.key !== 'Enter') return
    e.preventDefault()
    if (row === lines.length - 1) { addLine(col); return }
    gridRef.current?.querySelector(`[data-cell="${row + 1}-${col}"]`)?.focus()
  }

  const changeProject = (text) => {
    setProjectText(text)
    const p = projects.find(x => projectLabel(x) === text || x.name === text)
    setHead(h => ({ ...h, project: p ? p.name : '' }))
  }

  const filled = useMemo(
    () => lines.filter(l => l.item_description.trim() || String(l.price) !== '' || l.id_rem.trim()),
    [lines],
  )

  // Does a line look already requested or bought? Asked, a moment after the typing stops, for the lines that
  // say something (a code, or a description of some length): open requests and SAP orders.
  const huella = (l) => `${l.id_rem.trim().toLowerCase()}|${l.item_description.trim().toLowerCase()}|${head.supplier.trim().toLowerCase()}`
  const huellas = lines.map(l => `${l.uid}:${huella(l)}`).join('\n')
  useEffect(() => {
    const pendientes = lines.filter(l => l.id_rem.trim().length >= 2 || l.item_description.trim().length >= 6)
    if (editId || !pendientes.length) return undefined        // editing: the request would match itself
    let vivo = true
    const h = setTimeout(() => {
      revisarDuplicados(pendientes.map(l => ({ id_rem: l.id_rem, descripcion: l.item_description, proveedor: head.supplier })))
        .then(r => {
          if (!vivo) return
          setAlertas(prev => {
            const n = { ...prev }
            pendientes.forEach((l, i) => {
              const x = r.lineas[i]
              if (x && x.total > 0) n[l.uid] = { huella: huella(l), total: x.total, coincidencias: x.coincidencias }
              else delete n[l.uid]
            })
            return n
          })
        })
        .catch(() => {})                   // the warning is a help: if it cannot be asked, the request is still written
    }, 700)
    return () => { vivo = false; clearTimeout(h) }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [huellas])

  // Other live requests of the same supplier: Compras sees them together automatically (information only).
  const [vigentes, setVigentes] = useState({ proveedor: '', solicitudes: [] })
  useEffect(() => {
    const prov = head.supplier.trim()
    if (prov.length < 3) return undefined
    let vivo = true
    const h = setTimeout(() => {
      getProveedorVigente(prov).then(r => { if (vivo) setVigentes({ proveedor: prov, solicitudes: r.solicitudes }) }).catch(() => {})
    }, 700)
    return () => { vivo = false; clearTimeout(h) }
  }, [head.supplier])
  const vigentesAhora = vigentes.proveedor === head.supplier.trim() ? vigentes.solicitudes.filter(s => s.id !== editId) : []

  // Alerts still standing for what the line says now, and which of them have no decision yet.
  const alertaDe = (l) => (alertas[l.uid]?.huella === huella(l) ? alertas[l.uid] : null)
  const decisionDe = (l) => (decisiones[l.uid]?.huella === huella(l) ? decisiones[l.uid] : null)
  const sinResolver = filled.filter(l => alertaDe(l) && !decisionDe(l))
  const continuar = (l, motivo) => setDecisiones(d => {
    const n = { ...d }
    if (motivo) n[l.uid] = { huella: huella(l), motivo }; else delete n[l.uid]
    return n
  })
  // Suggestions from SAP's suppliers' catalog (the last load): picking one fills its name and sets the order type
  // (PE foreign, PN domestic). Free typing always stays possible.
  const [proveedorSap, setProveedorSap] = useState(null)         // the supplier picked from the catalog
  const elegirProveedor = (p) => {
    setHead(h => ({ ...h, supplier: p.nombre || h.supplier }))
    setProveedorSap(p)
    if (!editId && p.tipo === 'PE') setTipo('international')
    if (!editId && p.tipo === 'PN') setTipo('national')
  }

  const quitarPorDuplicado = (l) => {
    setQuitadas(q => [...q, { item_cod: l.id_rem.trim() || null, descripcion: l.item_description.trim() || null, coincidencias: alertaDe(l)?.coincidencias || [] }])
    setLines(ls => (ls.length === 1 ? [emptyLine()] : ls.filter(x => x.uid !== l.uid)))
  }

  const totals = useMemo(() => lineTotals({
    tax_rate: 0.19, international: intl, vat_included: !intl && pie.vat_included, currency,
    discount: Number(pie.discount) || 0, is_multi: true,
    items: filled.map(l => ({
      quantity: Number(l.quantity || 0), currency: mixta ? l.currency : currency,
      estimated_unit_price: l.price === '' ? null : Number(l.price),
    })),
  }), [filled, intl, pie.vat_included, pie.discount, currency, mixta])

  const lineTotal = (l) => (l.price === '' ? null : Number(l.price) * Number(l.quantity || 0))

  const agregarArchivos = (lista) => {
    const nuevos = [...(lista || [])]
    const grandes = nuevos.filter(f => f.size > 10 * 1024 * 1024)
    if (grandes.length) setError(t('newRequest.attach.tooBig', { name: grandes[0].name }))
    setArchivos(a => [...a, ...nuevos.filter(f => f.size <= 10 * 1024 * 1024 && f.size > 0).map(file => ({ id: ++_uid, file }))])
  }

  // Once the request exists, its files go up one by one; a failure is told, never lost silently.
  const subirArchivos = async (id) => {
    const fallidos = []
    for (const a of archivos) {
      try { await uploadPurchaseAttachment(id, a.file) } catch { fallidos.push(a.file.name) }
    }
    if (fallidos.length) window.alert(t('newRequest.attach.failed', { names: fallidos.join(', ') }))
  }

  const submit = async () => {
    setError('')
    if (!head.project) { setError(t('modal.errors.projectRequired')); return }
    if (!head.area) { setError(t('modal.errors.areaRequired')); return }
    if (!filled.length) { setError(t('modal.errors.addOneItem')); return }
    for (let i = 0; i < filled.length; i++) {
      const l = filled[i]
      if (!l.item_description.trim()) { setError(t('modal.errors.itemDescRequired', { n: i + 1 })); return }
      if (!l.quantity || Number(l.quantity) <= 0) { setError(t('modal.errors.itemQtyRequired', { n: i + 1 })); return }
      if (!(Number(l.price) > 0)) { setError(t('modal.errors.itemPriceRequired', { n: i + 1 })); return }
    }
    const single = filled.length === 1
    if (single && tipo !== "service" && !filled[0].id_rem.trim()) { setError(t('modal.errors.idRemRequired')); return }
    if (sinResolver.length) { setError(t("newRequest.dup.sinResolver", { count: sinResolver.length })); return }

    const base = {
      order_type: tipo, international: intl, currency: mixta ? (original?.currency || currency) : currency, tax_rate: 0.19,
      vat_included: !intl && !!pie.vat_included, discount: Number(pie.discount) || 0,
      project_reference: head.project, area: head.area,
      supplier: head.supplier.trim() || null, needed_by: head.needed_by || null,
      justification: pie.justification.trim() || null, url: pie.url.trim() ? externalHref(pie.url) : null,
    }
    const price = (l) => (l.price === '' ? null : Number(l.price))
    const payload = single
      ? { ...base, is_multi: false, items: [], id_rem: tipo === "service" ? null : filled[0].id_rem.trim(),
          item_description: filled[0].item_description.trim(), quantity: Number(filled[0].quantity),
          unit: tipo === "service" ? null : (filled[0].unit || 'UND'), estimated_unit_price: price(filled[0]) }
      : { ...base, is_multi: true, items: filled.map(l => ({
          id_rem: l.id_rem.trim() || null, item_description: l.item_description.trim(),
          quantity: Number(l.quantity), unit: l.unit || 'UND', estimated_unit_price: price(l),
          final_unit_price: l.final ?? null, currency: mixta ? l.currency : currency })) }
    setSaving(true)
    try {
      if (editId) {
        await updatePurchase(editId, payload)
        await subirArchivos(editId)
        navigate(`/purchases/${editId}`)
        return
      }
      const created = await createPurchase(payload)
      await subirArchivos(created.id)
      // What was decided about each warning stays on record; it never holds the request back.
      const registro = [
        ...filled.filter(l => alertaDe(l) && decisionDe(l)).map(l => ({
          item_cod: l.id_rem.trim() || null, descripcion: l.item_description.trim(), opcion: 'continuar',
          motivo: decisionDe(l).motivo, coincidencias: alertaDe(l).coincidencias })),
        ...quitadas.map(q => ({ ...q, opcion: 'quitar' })),
      ]
      if (registro.length) await registrarDecisionesDuplicado(created.id, registro).catch(() => {})
      navigate(`/purchases/${created.id}`)
    } catch (e) {
      setError(e.response?.data?.detail || t('modal.errors.createFailed'))
      setSaving(false)
    }
  }

  const today = new Date().toLocaleDateString(currentLocale(), { day: 'numeric', month: 'short', year: 'numeric' })
  const singleLine = filled.length <= 1

  return (
    <div className="p-4 sm:p-8 max-w-6xl mx-auto">
      <div className="sticky top-0 z-10 -mx-4 sm:-mx-8 px-4 sm:px-8 py-3 mb-4 bg-white/95 backdrop-blur border-b border-gray-100 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">{editId ? t("newRequest.editTitle", { n: editId }) : t("modal.title")}</h1>
          <p className="text-xs text-gray-400">{editId ? t("newRequest.editHint") : t("newRequest.hint")}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => navigate(editId ? `/purchases/${editId}` : "/purchases")} className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200">
            {t('common:actions.cancel')}
          </button>
          <button onClick={submit} disabled={saving || (editId && !original)} className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-900 text-white hover:bg-gray-700 disabled:opacity-40">
            {saving ? t("common:actions.saving") : editId ? t("common:actions.save") : t("modal.createDraft")}
          </button>
        </div>
      </div>

      {error && <div role="alert" className="mb-4 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">{error}</div>}

      {/* Who and for what — left; the request's own data — right */}
      <section className="grid grid-cols-1 lg:grid-cols-5 gap-4 mb-5">
        <div className="lg:col-span-3 bg-white border border-gray-200 rounded-xl p-4 space-y-4">
          <div>
            <span className="block text-xs font-medium text-gray-600 mb-1">{t('newRequest.type')}</span>
            <div className="inline-flex rounded-lg border border-gray-200 p-0.5 bg-gray-50" role="radiogroup" aria-label={t('newRequest.type')}>
              {tipo === "service" && <span className="inline-block px-3 py-1.5 mr-2 rounded-md text-sm font-medium bg-violet-100 text-violet-700">{t("orderTypes.serviceShort")}</span>}
              {TYPES.map(id => (
                <button key={id} type="button" role="radio" aria-checked={tipo === id} disabled={!!editId} onClick={() => setTipo(id)}
                  className={`px-4 py-1.5 rounded-md text-sm font-medium ${tipo === id ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'}`}>
                  {t(`orderTypes.${id}Short`).replace(/ \(.*\)$/, '')}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-gray-400 mt-1">{t(`orderTypes.${tipo}Desc`)}</p>
          </div>
          <Field label={t('modal.fields.supplier')} hint={t('newRequest.supplierHint')}>
            <Autocompletar className={input} value={head.supplier} onChange={v => { setHead(h => ({ ...h, supplier: v })); setProveedorSap(null) }}
              buscar={q => buscarProveedoresSap(q).then(r => r.items)} alElegir={elegirProveedor}
              etiqueta={p => (
                <span className="flex items-center gap-2">
                  <span className={`text-[10px] font-semibold rounded px-1.5 py-0.5 ${p.tipo === 'PE' ? 'bg-amber-100 text-amber-800' : p.tipo === 'PN' ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-600'}`}>{p.tipo || '—'}</span>
                  <span className="truncate">{p.nombre}</span>
                  <span className="text-xs text-gray-400 shrink-0">{p.codigo}{p.rut ? ` · ${p.rut}` : ''}{p.activo ? '' : ` · ${t('newRequest.suggest.inactive')}`}</span>
                </span>
              )} />
            {proveedorSap && (
              <p className="text-[11px] text-gray-500 mt-1">
                {t('newRequest.suggest.supplierPicked', { code: proveedorSap.codigo, kind: proveedorSap.tipo === 'PE' ? t('orderTypes.internationalShort') : proveedorSap.tipo === 'PN' ? t('orderTypes.nationalShort') : '—' })}
              </p>
            )}
            {vigentesAhora.length > 0 && (
              <p className="text-[11px] text-sky-700 mt-1">
                {t('newRequest.supplierLive', { count: vigentesAhora.length })}{' '}
                {vigentesAhora.slice(0, 4).map(s => `#${s.id} ${s.proyecto || ''}`.trim()).join(', ')}
                {vigentesAhora.length > 4 ? '…' : ''}. {t('newRequest.supplierLiveHint')}
              </p>
            )}
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label={t('modal.fields.project')} hint={t('newRequest.projectHint')}>
              <input className={input} list="proyectos-sap" value={projectText} placeholder={t('newRequest.projectPlaceholder')}
                onChange={e => changeProject(e.target.value)} />
              <datalist id="proyectos-sap">{projects.map(p => <option key={p.id} value={projectLabel(p)} />)}</datalist>
              {projectText && !head.project && <span className="block text-[11px] text-amber-600 mt-1">{t('newRequest.projectPick')}</span>}
            </Field>
            <Field label={t('modal.fields.area')}>
              <select className={input} value={head.area} onChange={e => setHead(h => ({ ...h, area: e.target.value }))}>
                <option value="">{t('modal.fields.selectArea')}</option>
                {AREAS.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </Field>
          </div>
        </div>

        <div className="lg:col-span-2 bg-white border border-gray-200 rounded-xl p-4 space-y-3">
          <Row label={t('newRequest.status')}><span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-600">{t(`states.${original?.state || "draft"}`)}</span></Row>
          <Row label={t("newRequest.requester")}>{(editId ? original?.requester?.display_name : user?.display_name) || "—"}</Row>
          <Row label={t('newRequest.date')}>{today}</Row>
          <Field label={t('newRequest.neededBy')} hint={t('newRequest.neededByHint')}>
            <input type="date" className={input} value={head.needed_by} onChange={e => setHead(h => ({ ...h, needed_by: e.target.value }))} />
          </Field>
          <Field label={t('modal.fields.currency')}>
            {intl && !mixta ? (
              <select className={input} value={head.currency} onChange={e => setHead(h => ({ ...h, currency: e.target.value }))}>
                {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            ) : <div className="border border-gray-100 bg-gray-50 rounded-lg px-3 py-2 text-sm text-gray-500">{mixta ? t("newRequest.mixedCurrencies") : "CLP"}</div>}
          </Field>
        </div>
      </section>

      {/* Lines */}
      <section className="bg-white border border-gray-200 rounded-xl mb-5">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-gray-800">{t('modal.items.title')} <span className="text-gray-400 font-normal">({filled.length})</span></h2>
          <span className="text-[11px] text-gray-400 hidden sm:block">{t('newRequest.gridHint')}</span>
        </div>
        <div className="overflow-x-auto" ref={gridRef}>
          <table className="w-full min-w-[44rem] text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 bg-gray-50">
                <th className="px-2 py-2 w-8">#</th>
                <th className="px-2 py-2 w-36">{t('newRequest.cols.code')}{singleLine ? ' *' : ''}</th>
                <th className="px-2 py-2">{t('modal.items.cols.description')}</th>
                <th className="px-2 py-2 w-24">{t('modal.items.cols.qty')}</th>
                <th className="px-2 py-2 w-28">{t('modal.items.cols.unit')}</th>
                <th className="px-2 py-2 w-32">{t('modal.items.cols.unitPrice')} ({currency})</th>
                <th className="px-2 py-2 w-32 text-right">{t('newRequest.cols.total')}</th>
                <th className="w-8" />
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => {
                const lt = lineTotal(l)
                const alerta = alertaDe(l)
                return (
                  <Fragment key={l.uid}>
                  <tr className="border-t border-gray-100">
                    <td className="px-2 text-gray-400">{i + 1}</td>
                    <td><input className={cell} data-cell={`${i}-id_rem`} value={l.id_rem} onChange={e => setLine(i, { id_rem: e.target.value })} onKeyDown={e => onCellKey(e, i, 'id_rem')} aria-label={t('newRequest.cols.code')} /></td>
                    <td><input className={cell} data-cell={`${i}-item_description`} value={l.item_description} onChange={e => setLine(i, { item_description: e.target.value })} onKeyDown={e => onCellKey(e, i, 'item_description')} aria-label={t('modal.items.cols.description')} /></td>
                    <td><input className={cell} type="number" min="0" step="any" data-cell={`${i}-quantity`} value={l.quantity} onChange={e => setLine(i, { quantity: e.target.value })} onKeyDown={e => onCellKey(e, i, 'quantity')} aria-label={t('modal.items.cols.qty')} /></td>
                    <td>
                      <select className={cell} data-cell={`${i}-unit`} value={l.unit} onChange={e => setLine(i, { unit: e.target.value })} aria-label={t('modal.items.cols.unit')}>
                        {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                      </select>
                    </td>
                    <td><input className={cell} type="number" min="0" step="any" data-cell={`${i}-price`} value={l.price} onChange={e => setLine(i, { price: e.target.value })} onKeyDown={e => onCellKey(e, i, 'price')} aria-label={t('modal.items.cols.unitPrice')} /></td>
                    <td className="px-2 text-right text-gray-700 whitespace-nowrap">{lt == null ? '—' : fmtMoney(lt, mixta ? l.currency : currency)}</td>
                    <td>
                      <button type="button" onClick={() => removeLine(i)} className="px-2 text-gray-300 hover:text-red-600 text-lg leading-none" title={t('modal.items.removeItem')} aria-label={t('modal.items.removeItem')}>×</button>
                    </td>
                  </tr>
                  {alerta && (
                    <tr className="bg-amber-50/40">
                      <td />
                      <td colSpan={7} className="px-2 py-2">
                        <AlertaDuplicado alerta={alerta} decision={decisionDe(l)} t={t}
                          onContinuar={(m) => continuar(l, m)} onQuitar={() => quitarPorDuplicado(l)} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2 border-t border-gray-100">
          <button type="button" onClick={() => addLine()} className="text-sm font-medium text-gray-600 hover:text-gray-900">{t('modal.items.addItem')}</button>
        </div>
      </section>

      {/* Comments and totals */}
      <section className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3 space-y-4">
          <Field label={t('modal.fields.justification')} hint={t('newRequest.justificationHint')}>
            <textarea rows={4} className={input} value={pie.justification} onChange={e => setPie(p => ({ ...p, justification: e.target.value }))} />
          </Field>
          <Field label={t('newRequest.link')}>
            <input type="url" className={input} placeholder="https://…" value={pie.url} onChange={e => setPie(p => ({ ...p, url: e.target.value }))} />
          </Field>
          <div>
            <span className="block text-xs font-medium text-gray-600 mb-1">{t('newRequest.attach.title')}</span>
            <div onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); agregarArchivos(e.dataTransfer.files) }}
              className="border border-dashed border-gray-300 rounded-lg px-3 py-3 text-sm text-gray-500 bg-gray-50/50">
              <input ref={archivoRef} type="file" multiple hidden onChange={e => { agregarArchivos(e.target.files); e.target.value = '' }} />
              <button type="button" onClick={() => archivoRef.current?.click()} className="px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-sm text-gray-700 hover:bg-gray-50">
                {t('newRequest.attach.pick')}
              </button>
              <span className="ml-2 text-xs text-gray-400">{t('newRequest.attach.hint')}</span>
              {(original?.attachments || []).length > 0 && (
                <p className="mt-2 text-xs text-gray-500">{t('newRequest.attach.existing', { count: original.attachments.length })}: {original.attachments.map(a => a.filename).join(', ')}</p>
              )}
              {archivos.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {archivos.map(a => (
                    <li key={a.id} className="flex items-center justify-between gap-2 text-xs text-gray-700">
                      <span className="truncate">{a.file.name} <span className="text-gray-400">· {Math.max(1, Math.round(a.file.size / 1024))} KB</span></span>
                      <button type="button" onClick={() => setArchivos(l => l.filter(x => x.id !== a.id))} className="text-gray-400 hover:text-red-600" aria-label={t('newRequest.attach.remove')}>×</button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
        <div className="lg:col-span-2 bg-white border border-gray-200 rounded-xl p-4 h-fit">
          {totals.mixed ? null : (
            <>
              <Row label={t('newRequest.beforeDiscount')}>{fmtMoney(totals.subtotal, currency)}</Row>
              <Row label={t('newRequest.discount', { currency })}>
                <input type="number" min="0" step="any" placeholder="0" aria-label={t('newRequest.discount', { currency })}
                  className="w-28 border border-gray-200 rounded-md px-2 py-1 text-sm text-right focus:outline-none focus:ring-2 focus:ring-gray-200"
                  value={pie.discount} onChange={e => setPie(p => ({ ...p, discount: e.target.value }))} />
              </Row>
              <Row label={intl ? t('modal.totals.vatNoneIntl') : t('modal.totals.vatPct')}>{fmtMoney(totals.vat, currency)}</Row>
              {!intl && (
                <label className="flex items-center gap-2 text-xs text-gray-500 py-1 cursor-pointer">
                  <input type="checkbox" className="accent-gray-900" checked={pie.vat_included} onChange={e => setPie(p => ({ ...p, vat_included: e.target.checked }))} />
                  {t('modal.fields.vatIncluded')}
                </label>
              )}
              <Row label={t('modal.totals.total')} strong>{fmtMoney(totals.total, currency)}</Row>
            </>
          )}
        </div>
      </section>
    </div>
  )
}
